import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Building2, Wifi, Droplets, Car, Zap, Accessibility, Shield, CheckCircle2, Store } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Seo } from "../components/Seo";
import { toast } from "sonner";
import { API } from "../App";

const FEATURES = [
  { icon: Car, title: "On-site Parking", description: "Security main gate and response unit" },
  { icon: Zap, title: "Standby Generator", description: "Uninterrupted power supply" },
  { icon: Accessibility, title: "Elevator Access", description: "For disabled access and elderly customers" },
  { icon: Droplets, title: "Water Supply", description: "Common areas and toilets included" },
  { icon: Wifi, title: "Internet Ready", description: "Provision for connectivity" },
  { icon: Shield, title: "24/7 Security", description: "Safe and secure premises" },
];

export default function SpaceAvailablePage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/space-inquiry`, formData);
      toast.success("Inquiry sent! Our team will get back to you shortly.", { id: "inquiry-status" });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send inquiry. Please try again.", { id: "inquiry-status" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Space Available — Office & Retail to Let"
        path="/space-available"
        description="Prime office and retail space to let at Nyali Plaza, Main Nyali Road, Mombasa. On-site parking, standby generator, elevator, water, internet and 24/7 security. Enquire today."
      />
      {/* Header */}
      <header className="sticky top-0 z-50 glass glass-dark border-b border-border/50">
        <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-3 flex items-center gap-3">
          <Link to="/" data-testid="back-to-home">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading font-bold text-lg leading-tight text-foreground">Space Available</h1>
            <p className="text-xs text-muted-foreground">Office & Retail Spaces</p>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary to-blue-900 text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200')] bg-cover bg-center opacity-20" />
        <div className="relative max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-secondary-foreground text-sm font-semibold mb-4">
              <Building2 className="w-4 h-4" />
              Now Leasing
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold mb-4 tracking-tight">
              Office & Retail Space
            </h2>
            <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
              Prime commercial space at Mombasa's premier shopping destination
            </p>
          </motion.div>
        </div>
      </section>

      <main className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-8 md:py-12">
        {/* Location Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="bg-card rounded-2xl border border-border/50 p-6 md:p-8 mb-8"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <MapPin className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-heading text-xl font-bold text-foreground mb-2">Prime Location</h3>
              <p className="text-muted-foreground mb-4">
                Opposite Nyali Cinemax, Main Nyali Road
              </p>
              <p className="text-foreground leading-relaxed">
                Nyali Plaza hosts a wide range of stores from Chandarana supermarket, bank ATMs, jewellers, 
                mobile phone and M-Pesa services, fashion boutiques, furniture and electronic appliances.
              </p>
            </div>
          </div>
        </motion.div>

        {/* What We Offer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mb-8"
        >
          <h3 className="font-heading text-2xl font-bold text-foreground mb-6 text-center">What We Offer</h3>
          <div className="bg-accent/50 rounded-2xl p-6 md:p-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Office or Retail Spaces</p>
                    <p className="text-sm text-muted-foreground">Various sizes available to suit your needs</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Water & Internet Provision</p>
                    <p className="text-sm text-muted-foreground">Ready infrastructure for connectivity</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Main Road Signage</p>
                    <p className="text-sm text-muted-foreground">Enhanced visibility facing the main road</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Flexible Sizes</p>
                    <p className="text-sm text-muted-foreground">Space varies to match your requirements</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Common Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mb-8"
        >
          <h3 className="font-heading text-2xl font-bold text-foreground mb-6 text-center">Common Features Included</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {FEATURES.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.05, duration: 0.3 }}
                className="bg-card rounded-2xl border border-border/50 p-4 md:p-6 text-center hover:border-primary/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-heading font-semibold text-foreground text-sm md:text-base mb-1">{feature.title}</h4>
                <p className="text-xs md:text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="bg-card rounded-2xl border border-border/50 p-6 md:p-8"
        >
          <div className="text-center mb-6">
            <h3 className="font-heading text-2xl font-bold text-foreground mb-2">Interested? Get in Touch</h3>
            <p className="text-muted-foreground">Viewing advised. Contact us to schedule a visit.</p>
          </div>

          <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Your Name</Label>
              <Input
                id="name"
                data-testid="inquiry-name"
                placeholder="Enter your name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="inquiry-email"
                  placeholder="you@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="inquiry-phone"
                  placeholder="0700 000 000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                data-testid="inquiry-message"
                placeholder="Tell us about your space requirements..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
              />
            </div>
            <Button type="submit" className="w-full rounded-full" data-testid="submit-inquiry" disabled={submitting}>
              {submitting ? "Sending..." : "Send Inquiry"}
            </Button>
          </form>
        </motion.div>

        {/* Back to Directory */}
        <div className="text-center mt-8">
          <Link to="/directory" data-testid="browse-stores">
            <Button variant="outline" className="rounded-full">
              <Store className="w-4 h-4 mr-2" />
              Browse Current Stores
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border mt-8">
        <div className="max-w-md mx-auto md:max-w-4xl lg:max-w-6xl px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>Nyali Plaza, Main Nyali Road, Mombasa, Kenya</span>
            </div>
            <a href="https://www.nyaliplaza.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              www.nyaliplaza.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
